import React, { useContext, useState } from 'react';

import clsx from 'clsx';

import {
  Box,
  ButtonBase,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { FiMaximize2, FiX } from 'react-icons/fi';

import { AIChatContext } from '../../context';
import { DOCUMENT_ANALYSIS_IDEAS } from '../../constants';
import { LailaThread } from '../thread/LailaThread';
import { LailaComposer } from '../composer/LailaComposer';
import { LailaWelcome } from '../LailaWelcome';
import { LailaVoice } from '../voice/LailaVoice';
import { getFromLS, saveToLS } from '../../../../utils/local-storage';
import { LailaOrb } from './LailaOrb';

type Mode = 'chat' | 'voice';

const MODES: { mode: Mode; label: string }[] = [
  { mode: 'chat', label: 'Chat' },
  { mode: 'voice', label: 'Voice' },
];

// The panel reopens in the mode it was left in.
const MODE_KEY = 'lailaPanelMode';

interface Props {
  onClose: () => void;
  // Opens the same conversation on the full Vyce AI page.
  onExpand: () => void;
}

// Laila docked at the side of any page: the same conversation as the Vyce AI
// page, typed or spoken.
export const LailaPanel: React.FC<Props> = ({ onClose, onExpand }) => {
  const classes = useStyles();
  const { messages, thinking, status, retry } = useContext(AIChatContext);
  const [mode, setMode] = useState<Mode>(() => (getFromLS(MODE_KEY) === 'voice' ? 'voice' : 'chat'));

  const switchMode = (next: Mode) => {
    setMode(next);
    saveToLS(MODE_KEY, next);
  };

  return (
    <Box className={classes.panel} role="dialog" aria-label="Laila, your AI assistant">
      <Box className={classes.header}>
        {/* In voice mode the big orb below is Laila. */}
        {mode === 'chat' && <LailaOrb size={36} halo={false} state={thinking ? 'thinking' : 'idle'} />}
        <Box flex={1} minWidth={0}>
          <Typography className={classes.name}>Laila</Typography>
          <Typography variant="caption" color={thinking && mode === 'chat' ? 'primary' : 'textSecondary'}>
            {mode === 'voice' ? 'Voice mode' : thinking ? 'typing…' : 'AI assistant'}
          </Typography>
        </Box>
        <Box className={classes.modes} role="tablist" aria-label="Talk to Laila by">
          {MODES.map(item => (
            <ButtonBase
              key={item.mode}
              role="tab"
              aria-selected={mode === item.mode}
              className={clsx(classes.mode, { [classes.modeActive]: mode === item.mode })}
              onClick={() => switchMode(item.mode)}
            >
              {item.label}
            </ButtonBase>
          ))}
        </Box>
        <Tooltip title="Open full screen">
          <IconButton size="small" onClick={onExpand} aria-label="Open Laila full screen">
            <FiMaximize2 size={18} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close (Esc)">
          <IconButton size="small" onClick={onClose} aria-label="Close Laila">
            <FiX size={20} />
          </IconButton>
        </Tooltip>
      </Box>

      {mode === 'voice' ? (
        <LailaVoice onType={() => switchMode('chat')} />
      ) : (
        <>
          <LailaThread
            className={classes.thread}
            messages={messages}
            thinking={thinking}
            status={status}
            onRetry={retry}
            emptyState={<LailaWelcome compact />}
          />

          <Box className={classes.composer}>
            <LailaComposer fileSuggestions={DOCUMENT_ANALYSIS_IDEAS} />
          </Box>
        </>
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    panel: {
      position: 'fixed',
      top: 76,
      right: 16,
      bottom: 16,
      zIndex: theme.zIndex.drawer + 1,
      width: 420,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      borderRadius: 20,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[12],
      animation: '$slideIn 240ms cubic-bezier(.2,.8,.2,1) both',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      // Phones: the whole screen, above the app bar.
      [theme.breakpoints.down('xs')]: {
        inset: 0,
        width: 'auto',
        borderRadius: 0,
        border: 0,
        zIndex: theme.zIndex.modal,
      },
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 10px 10px 14px',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    name: { fontWeight: 600, lineHeight: 1.2 },
    modes: {
      display: 'flex',
      flex: 'none',
      padding: 3,
      marginRight: 4,
      borderRadius: 18,
      backgroundColor: theme.palette.action.hover,
    },
    mode: {
      height: 30,
      padding: '0 12px',
      borderRadius: 15,
      fontSize: 14,
      fontWeight: 500,
      color: theme.palette.text.secondary,
      transition: 'background-color 150ms, color 150ms, box-shadow 150ms',
      '&:hover': { color: theme.palette.text.primary },
    },
    modeActive: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[1],
    },
    thread: { flex: 1 },
    composer: { padding: 8 },
    '@keyframes slideIn': {
      from: { opacity: 0, transform: 'translateX(24px)' },
      to: { opacity: 1, transform: 'none' },
    },
  })
);
